/* eslint-disable prettier/prettier */
import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  SimpleGrid,
  Spinner,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useTaxAnalysis, ITaxFaturamentoImpostoItem } from '../../../../../hooks/useTaxAnalysis';
import { TaxFaturamentoChart } from './TaxFaturamentoChart';
import {
  formatarEmReais,
  formatarPercentual,
  ITaxAnalysisHistory,
  calculateTaxPercentage,
} from '../../../../../types/taxAnalysis';

// Paleta de cores inspirada no design Apple
const APPLE_COLORS = {
  PIS: '#5E5CE6', // Índigo vibrante
  COFINS: '#30D158', // Verde Apple
  CSLL: '#FFD60A', // Amarelo dourado
  IRPJ: '#FF453A', // Vermelho coral
  ICMS: '#0A84FF', // Azul iOS
  ISS: '#FF9F0A', // Laranja Apple
  ISS_RETIDO: '#BF5AF2', // Roxo Apple
};

interface TaxImpostosDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyData: ITaxFaturamentoImpostoItem | null;
  dateStart?: string;
  dateEnd?: string;
}

const formatPeriodo = (dateStart?: string, dateEnd?: string) => {
  if (!dateStart || !dateEnd) return null;
  const start = moment(dateStart).format('DD/MM/YYYY');
  const end = moment(dateEnd).format('DD/MM/YYYY');
  return `${start} a ${end}`;
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        bg="rgba(255, 255, 255, 0.85)"
        backdropFilter="blur(12px)"
        p={4}
        borderRadius="12px"
        boxShadow="0 4px 24px rgba(0, 0, 0, 0.08)"
        border="1px solid"
        borderColor="rgba(255, 255, 255, 0.3)"
      >
        <Flex align="center" gap={2} mb={2}>
          <Box w={3} h={3} borderRadius="full" bg={payload[0].payload.color} />
          <Text fontWeight="600" fontSize="sm" color="gray.700">
            {payload[0].name}
          </Text>
        </Flex>
        <Text fontSize="xl" fontWeight="700" color="gray.900">
          {formatarEmReais(payload[0].value)}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {formatarPercentual(payload[0].payload.percentual)}
        </Text>
      </Box>
    );
  }
  return null;
};

const HistoryTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        bg="rgba(255, 255, 255, 0.85)"
        backdropFilter="blur(12px)"
        p={4}
        borderRadius="12px"
        boxShadow="0 4px 24px rgba(0, 0, 0, 0.08)"
        border="1px solid"
        borderColor="rgba(255, 255, 255, 0.3)"
        minW="160px"
      >
        <Text fontWeight="700" mb={3} color="gray.800" fontSize="sm">
          {label}
        </Text>
        {payload.map((entry: any) => (
          <Flex key={entry.name} align="center" justify="space-between" mb={1}>
            <Flex align="center" gap={2}>
              <Box w={2} h={2} borderRadius="full" bg={entry.color} />
              <Text fontSize="xs" color="gray.600">
                {entry.name}
              </Text>
            </Flex>
            <Text fontSize="xs" fontWeight="600" color="gray.800">
              {formatarPercentual(entry.value)}
            </Text>
          </Flex>
        ))}
      </Box>
    );
  }
  return null;
};

export function TaxImpostosDetailModal({
  isOpen,
  onClose,
  companyData,
  dateStart,
  dateEnd,
}: TaxImpostosDetailModalProps) {
  const { getTaxAnalysisHistoryByPeriod } = useTaxAnalysis();

  // Transformar dados do companyData para o formato esperado
  const taxData = companyData
    ? {
        id: companyData.TbEmpresa.id,
        external_id: companyData.TbEmpresa.external_id,
        razao_social: companyData.TbEmpresa.razao_social,
        cnpj: companyData.TbEmpresa.cnpj,
        total_faturamento: companyData.total_faturamento || 0,
        impostos: {
          pis: companyData.pis || 0,
          pis_percentual: calculateTaxPercentage(
            companyData.pis || 0,
            companyData.total_faturamento || 0,
          ),
          cofins: companyData.cofins || 0,
          cofins_percentual: calculateTaxPercentage(
            companyData.cofins || 0,
            companyData.total_faturamento || 0,
          ),
          csll: companyData.csll || 0,
          csll_percentual: calculateTaxPercentage(
            companyData.csll || 0,
            companyData.total_faturamento || 0,
          ),
          irpj: companyData.irpj || 0,
          irpj_percentual: calculateTaxPercentage(
            companyData.irpj || 0,
            companyData.total_faturamento || 0,
          ),
          icms: companyData.icms || 0,
          icms_percentual: calculateTaxPercentage(
            companyData.icms || 0,
            companyData.total_faturamento || 0,
          ),
          iss: companyData.iss || 0,
          iss_percentual: calculateTaxPercentage(
            companyData.iss || 0,
            companyData.total_faturamento || 0,
          ),
          iss_retido: companyData.iss_retido || 0,
          iss_retido_percentual: calculateTaxPercentage(
            companyData.iss_retido || 0,
            companyData.total_faturamento || 0,
          ),
          total_impostos: companyData.total_impostos || 0,
          total_percentual: calculateTaxPercentage(
            companyData.total_impostos || 0,
            companyData.total_faturamento || 0,
          ),
        },
      }
    : null;

  // Query para buscar histórico com período dinâmico
  const { data: historyData, isFetching: isLoadingHistory } = useQuery<ITaxAnalysisHistory[]>({
    queryKey: [
      'TaxAnalysisHistoryByPeriod',
      { companyId: companyData?.TbEmpresa?.external_id, dateStart, dateEnd },
    ],
    queryFn: getTaxAnalysisHistoryByPeriod,
    enabled: isOpen && !!companyData?.TbEmpresa?.external_id && !!dateStart && !!dateEnd,
  });

  if (!companyData || !taxData) return null;

  // Dados para o grafico de pizza (donut) com cores Apple
  const pieData = [
    {
      name: 'PIS',
      value: taxData.impostos.pis,
      percentual: taxData.impostos.pis_percentual,
      color: APPLE_COLORS.PIS,
    },
    {
      name: 'COFINS',
      value: taxData.impostos.cofins,
      percentual: taxData.impostos.cofins_percentual,
      color: APPLE_COLORS.COFINS,
    },
    {
      name: 'CSLL',
      value: taxData.impostos.csll,
      percentual: taxData.impostos.csll_percentual,
      color: APPLE_COLORS.CSLL,
    },
    {
      name: 'IRPJ',
      value: taxData.impostos.irpj,
      percentual: taxData.impostos.irpj_percentual,
      color: APPLE_COLORS.IRPJ,
    },
    {
      name: 'ICMS',
      value: taxData.impostos.icms,
      percentual: taxData.impostos.icms_percentual,
      color: APPLE_COLORS.ICMS,
    },
    {
      name: 'ISS',
      value: taxData.impostos.iss,
      percentual: taxData.impostos.iss_percentual,
      color: APPLE_COLORS.ISS,
    },
    {
      name: 'ISS Retido',
      value: taxData.impostos.iss_retido,
      percentual: taxData.impostos.iss_retido_percentual,
      color: APPLE_COLORS.ISS_RETIDO,
    },
  ].filter(item => item.value > 0);

  // Formatar dados do historico para o grafico de linha
  const formattedHistory = (historyData || []).map(item => ({
    ...item,
    mesFormatado: item.mes,
  }));

  // Determinar o título do gráfico baseado no período
  const getHistoryTitle = () => {
    if (!dateStart || !dateEnd) return 'Evolução Histórica';
    const start = moment(dateStart);
    const end = moment(dateEnd);
    const diffMonths = end.diff(start, 'months');

    if (diffMonths < 3) {
      return `Evolução Histórica de impostos (${diffMonths} meses)`;
    }
    if (diffMonths <= 6) {
      return `Evolução Histórica de impostos (${diffMonths} meses)`;
    }
    if (diffMonths <= 12) {
      return `Evolução Histórica de impostos (${diffMonths} meses)`;
    }
    return `Evolução Histórica de impostos (${diffMonths} meses)`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="lg" fontWeight="bold">
            Detalhes de Impostos e Faturamento - {taxData.razao_social}
          </Text>
          <Text fontSize="sm" color="gray.500">
            CNPJ: {taxData.cnpj}
          </Text>
          {dateStart && dateEnd && (
            <Text fontSize="sm" color="blue.500" fontWeight="500">
              Periodo: {formatPeriodo(dateStart, dateEnd)}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Resumo */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
            <Box p={4} bg="blue.50" borderRadius="lg">
              <Text fontSize="sm" color="gray.600">
                Faturamento Total
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="blue.600">
                {formatarEmReais(taxData.total_faturamento)}
              </Text>
            </Box>
            <Box p={4} bg="green.50" borderRadius="lg">
              <Text fontSize="sm" color="gray.600">
                Total de Impostos
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="green.600">
                {formatarEmReais(taxData.impostos.total_impostos)}
              </Text>
            </Box>
            <Box p={4} bg="orange.50" borderRadius="lg">
              <Text fontSize="sm" color="gray.600">
                % sobre Faturamento
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="orange.600">
                {formatarPercentual(taxData.impostos.total_percentual)}
              </Text>
            </Box>
          </SimpleGrid>

          {/* Grafico de Pizza (Donut) e Tabela */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
            {/* Grafico Donut - Estilo Apple */}
            <Box
              p={6}
              bg="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
              borderRadius="16px"
              boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
              border="1px solid rgba(0, 0, 0, 0.04)"
            >
              <Text fontSize="md" fontWeight="600" mb={4} color="gray.800">
                Composicao dos Impostos
              </Text>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={2}
                      animationBegin={0}
                      animationDuration={800}
                      label={({ name, percentual }) => `${name}: ${formatarPercentual(percentual)}`}
                    >
                      {pieData.map(entry => (
                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Flex justify="center" align="center" h="300px">
                  <Text color="gray.500">Sem dados de impostos para exibir</Text>
                </Flex>
              )}
            </Box>

            {/* Tabela de Impostos - Estilo Apple */}
            <Box
              p={6}
              bg="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
              borderRadius="16px"
              boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
              border="1px solid rgba(0, 0, 0, 0.04)"
            >
              <Text fontSize="md" fontWeight="600" mb={4} color="gray.800">
                Detalhamento por Imposto
              </Text>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Imposto</Th>
                      <Th isNumeric>Valor</Th>
                      <Th isNumeric>% Faturamento</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.PIS} borderRadius="full" />
                          PIS
                        </Flex>
                      </Td>
                      <Td isNumeric>{formatarEmReais(taxData.impostos.pis)}</Td>
                      <Td isNumeric>{formatarPercentual(taxData.impostos.pis_percentual)}</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.COFINS} borderRadius="full" />
                          COFINS
                        </Flex>
                      </Td>
                      <Td isNumeric>{formatarEmReais(taxData.impostos.cofins)}</Td>
                      <Td isNumeric>{formatarPercentual(taxData.impostos.cofins_percentual)}</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.CSLL} borderRadius="full" />
                          CSLL
                        </Flex>
                      </Td>
                      <Td isNumeric>{formatarEmReais(taxData.impostos.csll)}</Td>
                      <Td isNumeric>{formatarPercentual(taxData.impostos.csll_percentual)}</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.IRPJ} borderRadius="full" />
                          IRPJ
                        </Flex>
                      </Td>
                      <Td isNumeric>{formatarEmReais(taxData.impostos.irpj)}</Td>
                      <Td isNumeric>{formatarPercentual(taxData.impostos.irpj_percentual)}</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.ICMS} borderRadius="full" />
                          ICMS
                        </Flex>
                      </Td>
                      <Td isNumeric>{formatarEmReais(taxData.impostos.icms)}</Td>
                      <Td isNumeric>{formatarPercentual(taxData.impostos.icms_percentual)}</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.ISS} borderRadius="full" />
                          ISS
                        </Flex>
                      </Td>
                      <Td isNumeric>{formatarEmReais(taxData.impostos.iss)}</Td>
                      <Td isNumeric>{formatarPercentual(taxData.impostos.iss_percentual)}</Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.ISS_RETIDO} borderRadius="full" />
                          ISS Retido
                        </Flex>
                      </Td>
                      <Td isNumeric>{formatarEmReais(taxData.impostos.iss_retido)}</Td>
                      <Td isNumeric>
                        {formatarPercentual(taxData.impostos.iss_retido_percentual)}
                      </Td>
                    </Tr>
                    <Tr bg="gray.100" fontWeight="bold">
                      <Td>TOTAL</Td>
                      <Td isNumeric>{formatarEmReais(taxData.impostos.total_impostos)}</Td>
                      <Td isNumeric>{formatarPercentual(taxData.impostos.total_percentual)}</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </SimpleGrid>

          {/* Grafico de Historico - Estilo Apple */}
          <Box
            p={6}
            bg="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
            borderRadius="16px"
            boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
            border="1px solid rgba(0, 0, 0, 0.04)"
          >
            <Text fontSize="md" fontWeight="600" mb={4} color="gray.800">
              {getHistoryTitle()}
            </Text>
            {isLoadingHistory && (
              <Flex justify="center" align="center" h="300px">
                <Spinner size="lg" color="blue.500" />
              </Flex>
            )}
            {!isLoadingHistory && formattedHistory.length > 0 && (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={formattedHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="mesFormatado" tick={{ fontSize: 12, fill: '#6B7280' }} />
                  <YAxis
                    tickFormatter={value => `${value}%`}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                  />
                  <Tooltip content={<HistoryTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total_percentual"
                    name="Total %"
                    stroke="#1F2937"
                    strokeWidth={2.5}
                    dot={{ fill: '#fff', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    animationBegin={0}
                    animationDuration={800}
                  />
                  <Line
                    type="monotone"
                    dataKey="pis_percentual"
                    name="PIS %"
                    stroke={APPLE_COLORS.PIS}
                    strokeWidth={2}
                    dot={{ fill: '#fff', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="cofins_percentual"
                    name="COFINS %"
                    stroke={APPLE_COLORS.COFINS}
                    strokeWidth={2}
                    dot={{ fill: '#fff', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="csll_percentual"
                    name="CSLL %"
                    stroke={APPLE_COLORS.CSLL}
                    strokeWidth={2}
                    dot={{ fill: '#fff', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="irpj_percentual"
                    name="IRPJ %"
                    stroke={APPLE_COLORS.IRPJ}
                    strokeWidth={2}
                    dot={{ fill: '#fff', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="icms_percentual"
                    name="ICMS %"
                    stroke={APPLE_COLORS.ICMS}
                    strokeWidth={2}
                    dot={{ fill: '#fff', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="iss_percentual"
                    name="ISS %"
                    stroke={APPLE_COLORS.ISS}
                    strokeWidth={2}
                    dot={{ fill: '#fff', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="iss_retido_percentual"
                    name="ISS Retido %"
                    stroke={APPLE_COLORS.ISS_RETIDO}
                    strokeWidth={2}
                    dot={{ fill: '#fff', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
            {!isLoadingHistory && formattedHistory.length === 0 && (
              <Flex justify="center" align="center" h="300px">
                <Text color="gray.500">Sem dados historicos disponiveis</Text>
              </Flex>
            )}
          </Box>

          {/* Grafico de Evolucao do Faturamento */}
          <Box mt={6}>
            <TaxFaturamentoChart
              companyId={companyData?.TbEmpresa?.external_id}
              dateStart={dateStart}
              dateEnd={dateEnd}
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
